import React, { useState } from 'react';
import { motion } from 'motion/react';
import { trackEvent } from '@/utils/analytics';
import { ChevronRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { QUESTIONS } from '@/data/diagnosticData';
import { Turnstile } from '@/components/ui/Turnstile';

export const EnneagramDiagnostic = () => {
  const [step, setStep] = useState<'intro' | 'quiz' | 'gate'>('intro');
  const [currentQ, setCurrentQ] = useState(0);
  const [scores, setScores] = useState<Record<number, number>>({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [consent, setConsent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleStart = () => {
    trackEvent('diagnostic_start', { type: 'enneagram_comprehensive' });
    setStep('quiz');
  };

  const handleAnswer = (value: number) => {
    const question = QUESTIONS[currentQ];
    setScores(prev => ({ ...prev, [question.type]: prev[question.type] + value }));
    
    if (currentQ < QUESTIONS.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      trackEvent('diagnostic_completed', { type: 'enneagram_comprehensive' });
      setStep('gate');
    }
  };

  const getPrimaryType = () => {
    let maxType = 1;
    let maxScore = -1;
    Object.entries(scores).forEach(([type, score]) => {
      if (score > maxScore) {
        maxScore = score;
        maxType = parseInt(type);
      }
    });
    return maxType;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name || !role) return;
    if (!consent) {
      setError('Please consent to the privacy policy to submit your request.');
      return;
    }
    if (!turnstileToken) {
      setError('Please complete the Turnstile verification.');
      return;
    }
    
    setLoading(true);
    setError(null);

    const primaryType = getPrimaryType();
    const totalScore = Object.values(scores).reduce((a, b) => a + b, 0);
    let resultToken = '';

    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source: 'diagnostic',
          fullName: name.trim(),
          email: email.trim().toLowerCase(),
          role: role.trim(),
          consent,
          primaryType,
          totalScore,
          scores,
          turnstileToken,
          company_website: companyWebsite,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (data && data.resultToken) {
        resultToken = data.resultToken;
      }
      if (!response.ok || data.error) {
        setError(data.error || 'Notice: Could not save your diagnostic lead, but your results are ready.');
      }
    } catch (err: any) {
      console.warn('Diagnostic lead submit network notice:', err?.message || err);
      setError('Notice: Could not save your diagnostic lead, but your results are ready.');
      setTurnstileToken('');
      setResetKey(prev => prev + 1);
    } finally {
      if (!resultToken) {
        resultToken = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
      }

      // Save diagnostic result and resultToken in browser localStorage
      try {
        localStorage.setItem(`fl_diagnostic_${resultToken}`, JSON.stringify({ primaryType, totalScore, scores }));
        const existingTokensStr = localStorage.getItem('fl_result_tokens');
        let tokens: string[] = [];
        if (existingTokensStr) {
          tokens = JSON.parse(existingTokensStr);
          if (!Array.isArray(tokens)) tokens = [];
        }
        if (!tokens.includes(resultToken)) {
          tokens.unshift(resultToken);
        }
        if (tokens.length > 20) {
          tokens = tokens.slice(0, 20);
        }
        localStorage.setItem('fl_result_tokens', JSON.stringify(tokens));
      } catch (storageErr) {
        console.warn('LocalStorage notice:', storageErr);
      }

      setLoading(false);
      navigate(`/diagnostic/results?token=${encodeURIComponent(resultToken)}`, {
        state: { primaryType, totalScore, scores, resultToken }
      });
    }
  };

  const renderIntro = () => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto text-center py-12"
    >
      <h1 className="text-4xl lg:text-5xl font-serif text-ink mb-6">Free Team Friction Diagnostic</h1>
      <p className="text-lg text-ink/80 mb-8 leading-relaxed max-w-2xl mx-auto">
        18 quick questions about how your team works under pressure. You'll see the friction pattern most likely holding your team back, where it comes from, and what to do about it. About 3 minutes.
      </p>
      <div className="flex flex-col items-center gap-4">
        <button 
          onClick={handleStart}
          className="bg-ink text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-rule-3 transition-colors inline-flex items-center gap-2"
        >
          Start the diagnostic <ChevronRight className="w-4 h-4" />
        </button>
        <p className="text-sm text-ink/70 mt-2">
          Looking for yourself rather than your team?{' '}
          <Link to="/mini-assessment" className="text-rule-3 hover:underline font-medium">
            Take the individual mini assessment
          </Link>
        </p>
      </div>
    </motion.div>
  );

  const renderQuiz = () => {
    const q = QUESTIONS[currentQ];
    const progress = ((currentQ + 1) / QUESTIONS.length) * 100;

    return (
      <motion.div 
        key={currentQ}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="max-w-2xl mx-auto py-12"
      >
        <div className="mb-8">
          <div className="flex justify-between text-xs uppercase tracking-wider text-ink/70 mb-2">
            <span>Question {currentQ + 1} of {QUESTIONS.length}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <div className="w-full bg-ground-deep h-1 rounded-full overflow-hidden">
            <div className="bg-rule-3 h-full transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <h2 className="text-2xl font-serif text-ink mb-8 leading-snug">{q.text}</h2>

        <div className="space-y-4">
          {[
            { label: 'Strongly Disagree', value: 1 },
            { label: 'Disagree', value: 2 },
            { label: 'Neutral', value: 3 },
            { label: 'Agree', value: 4 },
            { label: 'Strongly Agree', value: 5 },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleAnswer(opt.value)}
              className="w-full text-left p-4 bg-white border border-ground-deep hover:border-rule-3 hover:bg-ground-alt transition-colors text-ink font-medium text-sm flex items-center justify-between group"
            >
              <span>{opt.label}</span>
              <div className="w-4 h-4 rounded-full border border-ground-deep group-hover:border-rule-3 group-hover:bg-rule-3/25 transition-colors" />
            </button>
          ))}
        </div>
      </motion.div>
    );
  };

  const renderGate = () => (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="max-w-xl mx-auto py-12 bg-white border border-ground-deep p-8 md:p-10 shadow-xl"
    >
      <h2 className="text-3xl font-serif text-ink mb-3 text-center">Your Results Are Ready</h2>
      <p className="text-ink/70 text-sm text-center mb-8">
        Enter your details below to generate your comprehensive FIVE LENS Enneagram report and receive your secure access link.
      </p>

      <form onSubmit={handleEmailSubmit} className="space-y-5">
        {/* Honeypot */}
        <input
          type="text"
          name="company_website"
          value={companyWebsite}
          onChange={(e) => setCompanyWebsite(e.target.value)}
          style={{ display: 'none' }}
          tabIndex={-1}
          autoComplete="off"
        />

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">Full Name</label>
          <input
            type="text"
            required
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={loading}
            className="w-full bg-ground border border-ground-deep p-3.5 text-ink text-sm outline-none focus:border-rule-3"
            placeholder="Alex Smith"
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">Email Address</label>
          <input
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={loading}
            className="w-full bg-ground border border-ground-deep p-3.5 text-ink text-sm outline-none focus:border-rule-3"
            placeholder="alex@example.com"
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">Your Role</label>
          <div className="relative">
            <select
              required
              value={role}
              onChange={e => setRole(e.target.value)}
              disabled={loading}
              className="w-full bg-ground border border-ground-deep p-3.5 text-ink text-sm outline-none focus:border-rule-3 appearance-none cursor-pointer"
            >
              <option value="">Select your role...</option>
              <option value="hr">HR / L&D Leader</option>
              <option value="exec">C-Suite / Executive</option>
              <option value="coach">Independent Coach / Consultant</option>
              <option value="manager">Team Manager</option>
              <option value="other">Other</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-ink">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
            </div>
          </div>
        </div>

        <div className="pt-2">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              id="diagnostic-consent"
              type="checkbox"
              name="consent"
              checked={consent}
              onChange={e => setConsent(e.target.checked)}
              disabled={loading}
              required
              className="w-4 h-4 mt-1 accent-rule-3 cursor-pointer border-ground-deep rounded"
            />
            <span className="text-xs text-ink/80 leading-relaxed">
              I agree to the{' '}
              <Link to="/privacy-policy" target="_blank" className="underline hover:text-rule-3 font-medium">
                Privacy Policy
              </Link>{' '}
              and consent to being contacted regarding my diagnostic report. <span className="text-rose-600">*</span>
            </span>
          </label>
        </div>

        {error && (
          <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs">
            {error}
          </div>
        )}

        <Turnstile 
          onVerify={(token) => { setTurnstileToken(token); setError(null); }}
          onExpire={() => { setTurnstileToken(''); setError('Verification failed, please try again'); setResetKey(prev => prev + 1); }}
          onError={() => { setTurnstileToken(''); setError('Verification failed, please try again'); setResetKey(prev => prev + 1); }}
          resetKey={resetKey} 
        />

        <button 
          type="submit" 
          disabled={loading || !turnstileToken}
          className="w-full bg-ink text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-ink/70 transition-colors mt-4 disabled:opacity-70 flex justify-center items-center gap-2"
        >
          {loading ? 'Processing Analysis...' : (!turnstileToken ? 'Verifying…' : 'Reveal My Diagnostic Report')}
          {!loading && turnstileToken && <ChevronRight className="w-4 h-4" />}
        </button>
      </form>
    </motion.div>
  );

  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
        {step === 'intro' && renderIntro()}
        {step === 'quiz' && renderQuiz()}
        {step === 'gate' && renderGate()}
      </div>
    </div>
  );
};
